'use client';

/**
 * LandingHeader.tsx — topplinje for de offentlige sidene
 *
 * Logo, meny, språkbryter (NO | EN), temabryter og «Logg inn». Fra lg vises
 * mega-menyen (MegaMenu); under lg vises hamburger og skuff (MobileMenu).
 * Språk, tema og innlogging er alltid synlige. Menyinnholdet kommer fra
 * masthead-content.ts og sendes inn av siden.
 */

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { Login, Moon, Sun } from '@carbon/icons-react';
import type { LandingContent, Locale, MastheadMenu } from '@/types/landing';
import { useTheme } from '@/hooks/useTheme';
import MegaMenu from './MegaMenu';
import MobileMenu from './MobileMenu';
import styles from './landing-header.module.css';

interface LandingHeaderProps {
  ui: LandingContent['ui'];
  /** Menyen (Produkter-mega-meny + lenker); se masthead-content.ts */
  menu: MastheadMenu;
  locale: Locale;
  /** «hero»: gjennomsiktig med hvit tekst over en blå hero (forsiden), solid etter hero eller ved åpen meny */
  variant?: 'solid' | 'hero';
  /** false: skjuler temabryteren (sider som alltid er mørke, f.eks. /forside-5) */
  showThemeToggle?: boolean;
  onLocaleChange: (locale: Locale) => void;
}

const LOCALES: { value: Locale; label: string; name: string }[] = [
  { value: 'no', label: 'NO', name: 'Norsk' },
  { value: 'en', label: 'EN', name: 'English' },
];

export default function LandingHeader({ ui, menu, locale, variant = 'solid', showThemeToggle = true, onLocaleChange }: LandingHeaderProps) {
  const { theme, toggle } = useTheme();
  const [pastHero, setPastHero] = useState(false);

  // variant="hero": solid først når man har scrollet forbi hero-seksjonen ([data-hero])
  useEffect(() => {
    if (variant !== 'hero') return;
    const update = () => {
      const hero = document.querySelector<HTMLElement>('[data-hero]');
      setPastHero(window.scrollY > (hero ? hero.offsetHeight - 48 : 40));
    };
    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [variant]);

  const overHero = variant === 'hero' && !pastHero;
  const ThemeIcon = theme === 'dark' ? Sun : Moon;

  return (
    <header className={`${styles.header} ${overHero ? styles.overHero : ''}`}>
      <a href="#innhold" className={styles.skip}>
        {ui.skipToContent}
      </a>

      <div className={styles.inner}>
        <MobileMenu menu={menu} navLabel={ui.navLabel} />

        <Link href="/" className={styles.brand} aria-label="inSuite">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src="/Logo/inSuiteWhiteFontSVG.png" alt="" className={styles.logo} />
        </Link>

        <MegaMenu menu={menu} navLabel={ui.navLabel} />

        <div className={styles.actions}>
          <div className={styles.segment} role="group" aria-label={ui.languageLabel}>
            {LOCALES.map((l) => (
              <button
                key={l.value}
                type="button"
                lang={l.value}
                className={styles.segmentButton}
                aria-pressed={locale === l.value}
                aria-label={l.name}
                onClick={() => onLocaleChange(l.value)}
              >
                {l.label}
              </button>
            ))}
          </div>

          {showThemeToggle && (
            <button
              type="button"
              className={styles.iconButton}
              onClick={toggle}
              aria-label={theme === 'dark' ? ui.themeToLight : ui.themeToDark}
              title={ui.themeLabel}
            >
              <ThemeIcon size={20} aria-hidden />
            </button>
          )}

          <Link href="/login" className={styles.login} aria-label={ui.login}>
            <span className={styles.loginLabel} aria-hidden>
              {ui.login}
            </span>
            <Login size={20} aria-hidden />
          </Link>
        </div>
      </div>
    </header>
  );
}
